import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import {
  Plus,
  FileText,
  PaperPlaneTilt,
  Spinner,
  CalendarBlank,
  Trash,
  Eye,
  Warning,
  X
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = { USD: '$', XCD: 'EC$', JMD: 'J$', TTD: 'TT$' };

const STATUS_LABELS = {
  pending_approval: 'Pending Approval',
  approved: 'PO Approved',
  rejected: 'PO Rejected',
  po_issued: 'PO Issued',
  vendor_confirmed: 'Vendor Confirmed',
  documents_uploaded: 'Documents Uploaded',
  in_transit: 'In Transit',
  // Legacy mappings
  draft: 'Pending Approval',
  requisition_approved: 'Pending Approval'
};

const STATUS_COLORS = {
  pending_approval: 'bg-[#F4A261]/10 text-[#B45309]',
  approved: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  rejected: 'bg-[#E63946]/10 text-[#E63946]',
  po_issued: 'bg-[#0F52BA]/10 text-[#0F52BA]',
  vendor_confirmed: 'bg-[#38B2AC]/10 text-[#0F766E]',
  documents_uploaded: 'bg-[#F4A261]/10 text-[#B45309]',
  in_transit: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  // Legacy mappings
  draft: 'bg-slate-100 text-slate-600',
  requisition_approved: 'bg-slate-200 text-slate-700'
};

// Helper to check if PO is overdue (issued over 48 hours ago, pending confirmation)
const isPoOverdue = (po) => {
  if (po.status !== 'po_issued' || po.vendor_confirmation_status !== 'pending') return false;
  if (!po.issued_at) return false;
  const hoursSinceIssued = (new Date() - new Date(po.issued_at)) / (1000 * 60 * 60);
  return hoursSinceIssued > 48;
};

const PurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterOverdue, setFilterOverdue] = useState(searchParams.get('filter') === 'overdue');
  
  // Tax settings state
  const [propertyTaxSettings, setPropertyTaxSettings] = useState(null);
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [taxExemptReason, setTaxExemptReason] = useState('');

  const [formData, setFormData] = useState({
    requisition_id: '',
    vendor_id: '',
    required_delivery_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    currency: 'USD',
    payment_terms: 'Net 30',
    po_type: 'overseas',  // "overseas" or "local"
    notes: '',
    line_items: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Handle convert parameter from PR page
  useEffect(() => {
    const convertReqId = searchParams.get('convert');
    if (convertReqId && requisitions.length > 0) {
      const req = requisitions.find(r => r.id === convertReqId);
      if (req) {
        // Pre-populate the form with PR data
        const preferredVendorId = req.items[0]?.preferred_vendor_id || '';
        const vendor = vendors.find(v => v.id === preferredVendorId);
        
        setFormData(prev => ({
          ...prev,
          requisition_id: convertReqId,
          vendor_id: preferredVendorId,
          currency: vendor?.preferred_currency || 'USD',
          payment_terms: vendor?.payment_terms || 'Net 30',
          line_items: req.items.map(item => ({
            item_name: item.item_name,
            quantity: item.quantity,
            unit: item.unit,
            unit_price: item.estimated_unit_price || 0,
            total_price: item.quantity * (item.estimated_unit_price || 0)
          }))
        }));
        setDialogOpen(true);
        // Clear the convert parameter after processing
        setSearchParams({});
      }
    }
  }, [searchParams, requisitions, vendors]);

  const fetchData = async () => {
    try {
      const [poRes, reqRes, vendorRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchase-orders`, getAuthHeaders()),
        // Fetch pending conversion PRs for the dropdown
        axios.get(`${API_URL}/api/requisitions`, getAuthHeaders()),
        axios.get(`${API_URL}/api/vendors`, getAuthHeaders())
      ]);
      setPurchaseOrders(poRes.data);
      // Only show PRs that are pending conversion
      const pendingPRs = reqRes.data.filter(r => 
        ['pending', 'pending_conversion', 'queried'].includes(r.status)
      );
      setRequisitions(pendingPRs);
      setVendors(vendorRes.data);
      
      // Fetch property tax settings
      try {
        const propertyId = user?.property_ids?.[0] || 'grand-bay-resort';
        const taxRes = await axios.get(`${API_URL}/api/property-settings/${propertyId}`, getAuthHeaders());
        setPropertyTaxSettings(taxRes.data);
      } catch (err) {
        console.log('No property tax settings found');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequisitionSelect = (reqId) => {
    const req = requisitions.find(r => r.id === reqId);
    if (req) {
      setFormData(prev => ({
        ...prev,
        requisition_id: reqId,
        line_items: req.items.map(item => ({
          item_name: item.item_name,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.estimated_unit_price || 0,
          total_price: item.quantity * (item.estimated_unit_price || 0)
        }))
      }));
    }
  };

  const handleVendorSelect = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      setFormData(prev => ({
        ...prev,
        vendor_id: vendorId,
        payment_terms: vendor.payment_terms,
        currency: vendor.preferred_currency
      }));
    }
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        newItems[index].total_price = newItems[index].quantity * newItems[index].unit_price;
      }
      return { ...prev, line_items: newItems };
    });
  };

  const addLineItem = () => {
    if (formData.line_items.length >= 20) {
      toast.error('Maximum 20 line items allowed per purchase order');
      return;
    }
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        item_name: '',
        quantity: 1,
        unit: 'units',
        unit_price: 0,
        total_price: 0
      }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length <= 1) {
      toast.error('At least one line item is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    
    // For local POs with tax disabled, require a reason
    if (formData.po_type === 'local' && propertyTaxSettings?.tax_rate && !taxEnabled && !taxExemptReason.trim()) {
      toast.error('Please provide a reason for tax exemption');
      return;
    }
    
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        required_delivery_date: formData.required_delivery_date.toISOString()
      };
      
      // Add tax data for local POs
      if (formData.po_type === 'local' && propertyTaxSettings?.tax_rate) {
        submitData.tax_enabled = taxEnabled;
        submitData.tax_type = propertyTaxSettings.tax_type;
        submitData.tax_rate = propertyTaxSettings.tax_rate;
        submitData.tax_amount = taxEnabled ? totalValue * (propertyTaxSettings.tax_rate / 100) : 0;
        submitData.tax_exempt_reason = !taxEnabled ? taxExemptReason : null;
      }
      
      await axios.post(`${API_URL}/api/purchase-orders`, submitData, getAuthHeaders());
      toast.success('Purchase order created');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssuePO = async (poId) => {
    try {
      await axios.put(`${API_URL}/api/purchase-orders/${poId}/issue`, {}, getAuthHeaders());
      toast.success('Purchase order issued to vendor');
      fetchData();
    } catch (error) {
      toast.error('Failed to issue PO');
    }
  };

  const resetForm = () => {
    setFormData({
      requisition_id: '',
      vendor_id: '',
      required_delivery_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      currency: 'USD',
      payment_terms: 'Net 30',
      po_type: 'overseas',
      notes: '',
      line_items: []
    });
    setTaxEnabled(true);
    setTaxExemptReason('');
  };

  const viewPO = (po) => {
    navigate(`/purchase-orders/${po.id}`);
  };

  const totalValue = formData.line_items.reduce((sum, item) => sum + (item.total_price || 0), 0);
  
  // Calculate tax for local POs
  const taxRate = propertyTaxSettings?.tax_rate || 0;
  const taxAmount = (formData.po_type === 'local' && taxEnabled && taxRate) ? totalValue * (taxRate / 100) : 0;
  const grandTotal = totalValue + taxAmount;

  // Filter POs based on overdue filter
  const displayedPOs = filterOverdue 
    ? purchaseOrders.filter(po => isPoOverdue(po))
    : purchaseOrders;

  // Count overdue POs for display
  const overdueCount = purchaseOrders.filter(po => isPoOverdue(po)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Purchase Orders
          </h1>
          <p className="text-slate-500 mt-1">Create and manage purchase orders</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="new-po-btn">
              <Plus size={20} className="mr-2" />
              Create PO
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Approved Requisition</Label>
                  <Select value={formData.requisition_id} onValueChange={handleRequisitionSelect}>
                    <SelectTrigger data-testid="po-requisition-select">
                      <SelectValue placeholder="Select requisition" />
                    </SelectTrigger>
                    <SelectContent>
                      {requisitions.map(req => (
                        <SelectItem key={req.id} value={req.id}>
                          {req.department_name} - {req.budget_code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <Select value={formData.vendor_id} onValueChange={handleVendorSelect}>
                    <SelectTrigger data-testid="po-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left" data-testid="po-delivery-date">
                        <CalendarBlank size={18} className="mr-2" />
                        {format(formData.required_delivery_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.required_delivery_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, required_delivery_date: date || new Date() }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="XCD">XCD (EC$)</SelectItem>
                      <SelectItem value="JMD">JMD (J$)</SelectItem>
                      <SelectItem value="TTD">TTD (TT$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Select value={formData.payment_terms} onValueChange={(value) => setFormData(prev => ({ ...prev, payment_terms: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* PO Type Selection */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-sm font-semibold text-slate-700 mb-3 block">Purchase Order Type</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, po_type: 'overseas' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.po_type === 'overseas' 
                        ? 'border-[#0F52BA] bg-[#0F52BA]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="po-type-overseas"
                  >
                    <div className="text-left">
                      <p className={`font-semibold ${formData.po_type === 'overseas' ? 'text-[#0F52BA]' : 'text-slate-700'}`}>
                        Overseas PO
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, po_type: 'local' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.po_type === 'local' 
                        ? 'border-[#2D6A4F] bg-[#2D6A4F]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="po-type-local"
                  >
                    <div className="text-left">
                      <p className={`font-semibold ${formData.po_type === 'local' ? 'text-[#2D6A4F]' : 'text-slate-700'}`}>
                        Local PO
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Line Items</Label>
                  <span className="text-xs text-slate-500">
                    {formData.line_items.length} of 20 items
                  </span>
                </div>
                
                {formData.line_items.length > 0 ? (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Item Description</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 w-20">Qty</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 w-20">Unit</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 w-28">Unit Price</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 w-28">Line Total</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 w-16">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.line_items.map((item, index) => (
                          <tr key={index} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <Input
                                value={item.item_name}
                                onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                                placeholder="Enter item description"
                                className="h-8"
                                data-testid={`line-item-name-${index}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                className="h-8 w-full"
                                data-testid={`line-item-qty-${index}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                value={item.unit}
                                onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                                className="h-8 w-full"
                                data-testid={`line-item-unit-${index}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center">
                                <span className="text-slate-500 mr-1 text-sm">{CURRENCY_SYMBOLS[formData.currency]}</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.unit_price}
                                  onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                  className="h-8 w-full"
                                  data-testid={`line-item-price-${index}`}
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 font-medium text-slate-700">
                              {CURRENCY_SYMBOLS[formData.currency]}{(item.total_price || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {index > 0 ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeLineItem(index)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`remove-line-item-${index}`}
                                >
                                  <Trash size={16} />
                                </Button>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50">
                        <tr className="border-t border-slate-200">
                          <td colSpan="4" className="px-3 py-2 text-right text-sm text-slate-600">Subtotal:</td>
                          <td className="px-3 py-2 font-medium text-slate-700">
                            {CURRENCY_SYMBOLS[formData.currency]}{totalValue.toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                        
                        {/* Tax Section - Only for Local POs */}
                        {formData.po_type === 'local' && propertyTaxSettings?.tax_rate && (
                          <>
                            <tr className="border-t border-slate-100">
                              <td colSpan="4" className="px-3 py-3">
                                <div className="flex items-center justify-end gap-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-600">
                                      {propertyTaxSettings.tax_type || 'Tax'} ({propertyTaxSettings.tax_rate}%):
                                    </span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={taxEnabled}
                                        onChange={(e) => {
                                          setTaxEnabled(e.target.checked);
                                          if (e.target.checked) setTaxExemptReason('');
                                        }}
                                        className="sr-only peer"
                                        data-testid="tax-toggle"
                                      />
                                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0F52BA]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2D6A4F]"></div>
                                      <span className="ml-2 text-xs font-medium text-slate-500">
                                        {taxEnabled ? 'Enabled' : 'Exempt'}
                                      </span>
                                    </label>
                                  </div>
                                </div>
                                {!taxEnabled && (
                                  <div className="mt-2 flex justify-end">
                                    <div className="w-80">
                                      <Input
                                        placeholder="Reason for tax exemption (required)"
                                        value={taxExemptReason}
                                        onChange={(e) => setTaxExemptReason(e.target.value)}
                                        className="h-8 text-sm border-amber-300 focus:border-amber-500"
                                        data-testid="tax-exempt-reason"
                                      />
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 font-medium text-slate-700">
                                {taxEnabled ? (
                                  <span className="text-[#2D6A4F]">
                                    +{CURRENCY_SYMBOLS[formData.currency]}{taxAmount.toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-amber-600 text-sm">Exempt</span>
                                )}
                              </td>
                              <td></td>
                            </tr>
                          </>
                        )}
                        
                        <tr className="border-t border-slate-200">
                          <td colSpan="4" className="px-3 py-2 text-right font-semibold text-slate-800">
                            {formData.po_type === 'local' && propertyTaxSettings?.tax_rate ? 'Grand Total:' : 'Total:'}
                          </td>
                          <td className="px-3 py-2 font-bold text-[#0F52BA] text-lg">
                            {CURRENCY_SYMBOLS[formData.currency]}{grandTotal.toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center text-slate-500">
                    <FileText size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No line items yet. Select a requisition above or add items manually.</p>
                  </div>
                )}
                
                {/* Add Line Item Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addLineItem}
                  disabled={formData.line_items.length >= 20}
                  className="w-full border-dashed border-slate-300 text-slate-600 hover:text-[#0F52BA] hover:border-[#0F52BA]"
                  data-testid="add-line-item-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Add Line Item
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes for the vendor..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button type="button" variant="secondary" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !formData.requisition_id || !formData.vendor_id}
                  data-testid="create-po-btn"
                >
                  {submitting ? <Spinner size={20} className="animate-spin mr-2" /> : null}
                  Create Purchase Order
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Overdue Filter Banner */}
      {filterOverdue && (
        <div className="bg-[#E63946]/10 border border-[#E63946]/20 rounded-xl p-4 flex items-center justify-between" data-testid="overdue-filter-banner">
          <div className="flex items-center gap-3">
            <Warning size={20} weight="fill" className="text-[#E63946]" />
            <span className="font-medium text-[#E63946]">Showing only overdue POs awaiting vendor confirmation</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setFilterOverdue(false);
              setSearchParams({});
            }}
            className="text-[#E63946] hover:text-[#E63946] hover:bg-[#E63946]/10"
            data-testid="clear-overdue-filter-btn"
          >
            <X size={18} className="mr-1" />
            Clear Filter
          </Button>
        </div>
      )}

      {/* PO List */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {displayedPOs.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {filterOverdue ? 'No overdue purchase orders' : 'No purchase orders yet'}
            </p>
            {filterOverdue && (
              <Button 
                variant="link" 
                onClick={() => { setFilterOverdue(false); setSearchParams({}); }}
                className="mt-2 text-[#0F52BA]"
              >
                View all POs
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Delivery Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayedPOs.map(po => {
                  const overdue = isPoOverdue(po);
                  return (
                    <tr 
                      key={po.id} 
                      className={`hover:bg-slate-50/50 ${overdue ? 'bg-[#E63946]/5' : ''}`} 
                      data-testid={`po-row-${po.po_number}`}
                    >
                      <td className="px-4 py-4">
                        <p className={`font-medium ${overdue ? 'text-[#E63946]' : 'text-slate-800'}`}>{po.po_number}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(po.created_at).toLocaleDateString()}
                        </p>
                        {/* Currently With Indicator */}
                        {po.currently_with_name && (
                          <p className="text-xs mt-1" data-testid={`currently-with-${po.po_number}`}>
                            <span className="text-slate-500">Currently with: </span>
                            <span className={`font-medium ${
                              po.currently_with_action?.includes('OVERDUE') ? 'text-[#E63946]' : 'text-[#8B5CF6]'
                            }`}>
                              {po.currently_with_name}
                            </span>
                            <span className="text-slate-400"> — </span>
                            <span className={`${
                              po.currently_with_action?.includes('OVERDUE') ? 'text-[#E63946]' : 'text-slate-600'
                            }`}>
                              {po.currently_with_action}
                            </span>
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                      <td className="px-4 py-4 font-medium text-slate-800">
                        {CURRENCY_SYMBOLS[po.currency]}{po.total_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        {new Date(po.required_delivery_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${overdue ? 'bg-[#E63946] text-white' : STATUS_COLORS[po.status]}`}>
                            {STATUS_LABELS[po.status]}
                          </span>
                          {overdue && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-[#E63946]/10 text-[#E63946]" data-testid={`overdue-badge-${po.po_number}`}>
                              <Warning size={12} weight="fill" />
                              Overdue — Awaiting Vendor Response
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" onClick={() => viewPO(po)} data-testid={`view-po-${po.po_number}`}>
                            <Eye size={18} />
                          </Button>
                          {po.status === 'requisition_approved' && (
                            <Button
                              size="sm"
                              className="bg-[#2D6A4F] hover:bg-[#1E4D38]"
                              onClick={() => handleIssuePO(po.id)}
                              data-testid={`issue-po-${po.po_number}`}
                            >
                              <PaperPlaneTilt size={18} className="mr-1" />
                              Issue
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrdersPage;
